import React, { useEffect, useMemo, useState } from "react";

type QuestionType = "MCQ" | "Numerical" | "True/False";
type Difficulty = "Easy" | "Medium" | "Hard";
type QuestionStatus = "Published" | "Draft" | "Review";
type PageMode = "list" | "form";

type Question = {
  id: number;
  question: string;
  course: string;
  subject: string;
  chapter: string;
  topic: string;
  type: QuestionType;
  difficulty: Difficulty;
  marks: number;
  negativeMarks: number;
  status: QuestionStatus;
  createdBy: string;
  usage: number;
  accuracy: number;
  options: string[];
  answer: string;
  explanation: string;
};

type QuestionForm = Omit<Question, "id" | "createdBy" | "usage" | "accuracy"> & {
  createdBy?: string;
};

const API_BASE = "https://mediitacademy.com/api/admin/questions";

const courses = ["All", "NEET", "JEE Main", "Class 8", "Class 7", "Foundation"];
const subjects = ["All", "Biology", "Physics", "Chemistry", "Mathematics", "Science"];
const difficulties = ["All", "Easy", "Medium", "Hard"];
const statuses = ["All", "Published", "Draft", "Review"];

const emptyForm: QuestionForm = {
  question: "",
  course: "JEE Main",
  subject: "Physics",
  chapter: "",
  topic: "",
  type: "MCQ",
  difficulty: "Easy",
  marks: 4,
  negativeMarks: 1,
  status: "Draft",
  options: ["", "", "", ""],
  answer: "",
  explanation: "",
  createdBy: "Admin",
};

const normalizeQuestion = (item: any): Question => ({
  id: Number(item.id),
  question: item.question || item.questionText || "",
  course: item.course || "",
  subject: item.subject || "",
  chapter: item.chapter || "",
  topic: item.topic || "",
  type: (item.type || item.questionType || "MCQ") as QuestionType,
  difficulty: (item.difficulty || "Easy") as Difficulty,
  marks: Number(item.marks || 0),
  negativeMarks: Number(item.negativeMarks || 0),
  status: (item.status || "Draft") as QuestionStatus,
  createdBy: item.createdBy || "Admin",
  usage: Number(item.usage || item.usageCount || 0),
  accuracy: Number(item.accuracy || 0),
  options: Array.isArray(item.options) ? item.options : [],
  answer: item.answer || item.correctAnswer || "",
  explanation: item.explanation || "",
});

const AdminQuestionBank: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [pageMode, setPageMode] = useState<PageMode>("list");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<QuestionForm>(emptyForm);
  const [search, setSearch] = useState("");
  const [activeCourse, setActiveCourse] = useState("All");
  const [activeSubject, setActiveSubject] = useState("All");
  const [activeDifficulty, setActiveDifficulty] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  const loadQuestions = async () => {
    setLoading(true);
    setApiError("");
    try {
      const params = new URLSearchParams();
      if (activeCourse !== "All") params.set("course", activeCourse);
      if (activeSubject !== "All") params.set("subject", activeSubject);
      if (activeDifficulty !== "All") params.set("difficulty", activeDifficulty);
      if (activeStatus !== "All") params.set("status", activeStatus);
      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(`${API_BASE}?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || `Question API failed (${res.status})`);
      setQuestions(Array.isArray(data) ? data.map(normalizeQuestion) : []);
    } catch (error: any) {
      setApiError(error?.message || "Question loading failed");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourse, activeSubject, activeDifficulty, activeStatus]);

  const filteredQuestions = useMemo(() => questions, [questions]);

  const openCreatePage = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEditPage = (question: Question) => {
    setEditingId(question.id);
    setForm({
      question: question.question,
      course: question.course || "JEE Main",
      subject: question.subject || "Physics",
      chapter: question.chapter,
      topic: question.topic,
      type: question.type,
      difficulty: question.difficulty,
      marks: question.marks,
      negativeMarks: question.negativeMarks,
      status: question.status,
      options:
        question.type === "MCQ"
          ? [...question.options, "", "", "", ""].slice(0, 4)
          : question.type === "True/False"
          ? ["True", "False"]
          : [],
      answer: question.answer,
      explanation: question.explanation,
      createdBy: question.createdBy || "Admin",
    });
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToList = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("list");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTypeChange = (type: QuestionType) => {
    setForm((prev) => ({
      ...prev,
      type,
      options: type === "MCQ" ? ["", "", "", ""] : type === "True/False" ? ["True", "False"] : [],
      answer: "",
    }));
  };

  const handleOptionChange = (index: number, value: string) => {
    const updated = [...form.options];
    updated[index] = value;
    setForm({ ...form, options: updated });
  };

  const validateForm = () => {
    if (!form.question.trim() || !form.chapter.trim() || !form.topic.trim() || !form.answer.trim()) {
      alert("Please fill question, chapter, topic and correct answer.");
      return false;
    }

    if (form.type === "MCQ") {
      const validOptions = form.options.filter((option) => option.trim());
      if (validOptions.length < 2) {
        alert("MCQ me minimum 2 options required hain.");
        return false;
      }
      if (!validOptions.includes(form.answer)) {
        alert("Correct answer options me se hi hona chahiye.");
        return false;
      }
    }

    return true;
  };

  const handleSaveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const cleanedOptions =
      form.type === "MCQ" ? form.options.filter((option) => option.trim()) : form.type === "True/False" ? ["True", "False"] : [];

    const payload = { ...form, options: cleanedOptions };

    try {
      const res = await fetch(editingId ? `${API_BASE}/${editingId}` : API_BASE, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || `Question save failed (${res.status})`);
      backToList();
      await loadQuestions();
    } catch (error: any) {
      alert(error?.message || "Question save failed");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure? Ye question delete ho jayega.")) return;
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Question delete failed (${res.status})`);
      await loadQuestions();
    } catch (error: any) {
      alert(error?.message || "Question delete failed");
    }
  };

  if (pageMode === "form") {
    return (
      <div className="w-full max-w-full min-w-0 space-y-6 overflow-hidden">
        <section className="relative overflow-hidden rounded-[30px] bg-slate-950 p-6 text-white shadow-xl shadow-slate-300/40 sm:p-8">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="relative flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div>
              <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                {editingId ? "Edit Question" : "Add Question"}
              </div>
              <h1 className="text-2xl font-black tracking-tight sm:text-4xl">
                {editingId ? "Update question details" : "Create new question"}
              </h1>
              <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
                Question, options, correct answer, marks, difficulty aur explanation manage karo.
              </p>
            </div>
            <button onClick={backToList} type="button" className="w-fit rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50">
              ← Back to Question Bank
            </button>
          </div>
        </section>

        <form onSubmit={handleSaveQuestion} className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="space-y-6">
            <Card title="Question Details">
              <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Question</label>
              <textarea
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="Enter question here..."
                rows={5}
                className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
              />
              <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <SelectInput label="Course" value={form.course} options={courses.filter((i) => i !== "All")} onChange={(value) => setForm({ ...form, course: value })} />
                <SelectInput label="Subject" value={form.subject} options={subjects.filter((i) => i !== "All")} onChange={(value) => setForm({ ...form, subject: value })} />
                <SelectInput label="Question Type" value={form.type} options={["MCQ", "Numerical", "True/False"]} onChange={(value) => handleTypeChange(value as QuestionType)} />
                <SelectInput label="Difficulty" value={form.difficulty} options={["Easy", "Medium", "Hard"]} onChange={(value) => setForm({ ...form, difficulty: value as Difficulty })} />
                <FormInput label="Chapter" value={form.chapter} placeholder="Example: Laws of Motion" onChange={(value) => setForm({ ...form, chapter: value })} />
                <FormInput label="Topic" value={form.topic} placeholder="Example: Newton's Second Law" onChange={(value) => setForm({ ...form, topic: value })} />
              </div>
            </Card>

            <Card title="Answer Options">
              {form.type === "MCQ" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  {form.options.map((option, index) => (
                    <input key={index} value={option} onChange={(e) => handleOptionChange(index, e.target.value)} placeholder={`Option ${index + 1}`} className="h-12 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white" />
                  ))}
                </div>
              )}
              {form.type === "True/False" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-black text-slate-700">True</div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-black text-slate-700">False</div>
                </div>
              )}
              {form.type === "Numerical" && <div className="rounded-2xl bg-blue-50 p-4 text-sm font-bold text-blue-700">Numerical question me options nahi honge. Sirf answer fill karo.</div>}
              <div className="mt-5">
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Correct Answer</label>
                {form.type === "MCQ" || form.type === "True/False" ? (
                  <select value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white">
                    <option value="">Select correct answer</option>
                    {(form.type === "True/False" ? ["True", "False"] : form.options.filter(Boolean)).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} placeholder="Enter numerical answer" className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white" />
                )}
              </div>
            </Card>

            <Card title="Explanation / Solution">
              <textarea value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} placeholder="Write solution/explanation here..." rows={5} className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white" />
            </Card>
          </section>

          <aside className="space-y-6">
            <Card title="Publish Settings">
              <div className="grid gap-4 sm:grid-cols-3 2xl:grid-cols-1">
                <FormInput label="Marks" type="number" value={String(form.marks)} placeholder="4" onChange={(value) => setForm({ ...form, marks: Number(value) })} />
                <FormInput label="Negative Marks" type="number" value={String(form.negativeMarks)} placeholder="1" onChange={(value) => setForm({ ...form, negativeMarks: Number(value) })} />
                <SelectInput label="Status" value={form.status} options={["Published", "Draft", "Review"]} onChange={(value) => setForm({ ...form, status: value as QuestionStatus })} />
              </div>
            </Card>

            <Card title="Question Preview">
              <p className="text-xs font-black uppercase tracking-wide text-slate-400">{form.course} • {form.subject} • {form.difficulty}</p>
              <h3 className="mt-3 break-words text-sm font-black leading-6 text-slate-950">{form.question || "Question preview will appear here..."}</h3>
              {form.type === "MCQ" && <div className="mt-4 space-y-2">{form.options.filter(Boolean).map((option, index) => <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">{String.fromCharCode(65 + index)}. {option}</div>)}</div>}
              {form.answer && <div className="mt-4 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700">Answer: {form.answer}</div>}
            </Card>

            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-xl 2xl:sticky 2xl:bottom-5">
              <div className="grid gap-3 sm:grid-cols-3 2xl:grid-cols-1">
                <button type="submit" className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700">{editingId ? "Update Question" : "Create Question"}</button>
                <button type="button" onClick={() => setForm({ ...form, status: "Draft" })} className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">Save as Draft</button>
                <button type="button" onClick={backToList} className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-200">Cancel</button>
              </div>
            </div>
          </aside>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full min-w-0 space-y-6 overflow-hidden">
      <section className="relative overflow-hidden rounded-[30px] bg-slate-950 p-6 text-white shadow-xl shadow-slate-300/40 sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="relative flex flex-col justify-between gap-5 xl:flex-row xl:items-center">
          <div>
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">Question Bank Management</div>
            <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">Manage questions, answers and test content</h1>
            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">Admin/Teacher ke questions yahi save honge. Student ko direct question bank nahi dikhega.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={loadQuestions} className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">Refresh</button>
            <button onClick={openCreatePage} className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50">+ Add Question</button>
          </div>
        </div>
      </section>

      {apiError && <div className="rounded-[22px] border border-red-100 bg-red-50 p-4 text-sm font-black text-red-700">{apiError}</div>}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total Questions" value={String(questions.length)} note="Backend saved" />
        <StatCard title="Published" value={String(questions.filter((q) => q.status === "Published").length)} note="Ready for tests" />
        <StatCard title="In Review" value={String(questions.filter((q) => q.status === "Review").length)} note="Need approval" />
        <StatCard title="Drafts" value={String(questions.filter((q) => q.status === "Draft").length)} note="Incomplete questions" />
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
          <FilterButtons title="Course" options={courses} active={activeCourse} onChange={setActiveCourse} />
          <FilterButtons title="Subject" options={subjects} active={activeSubject} onChange={setActiveSubject} />
          <FilterButtons title="Difficulty" options={difficulties} active={activeDifficulty} onChange={setActiveDifficulty} />
          <FilterButtons title="Status" options={statuses} active={activeStatus} onChange={setActiveStatus} />
          <div>
            <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">Search</p>
            <div className="flex h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4">
              <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadQuestions()} placeholder="Search question, chapter, topic..." className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400" />
              <button onClick={loadQuestions} className="text-xs font-black text-blue-700">GO</button>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col justify-between gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-xl font-black text-slate-950">All Questions</h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">{filteredQuestions.length} questions found</p>
            </div>
            <button onClick={openCreatePage} className="w-fit rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-700">+ Add New</button>
          </div>

          {loading ? (
            <div className="p-10 text-center text-sm font-black text-slate-500">Loading questions...</div>
          ) : (
            <div className="grid gap-4 p-5">
              {filteredQuestions.map((item) => (
                <div key={item.id} className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap gap-2">
                        <TypeBadge type={item.type} />
                        <DifficultyBadge difficulty={item.difficulty} />
                        <StatusBadge status={item.status} />
                      </div>
                      <h3 className="mt-3 break-words text-base font-black leading-6 text-slate-950">{item.question}</h3>
                      <p className="mt-1 text-xs font-bold text-slate-400">{item.course} • {item.subject} • {item.chapter} • {item.topic}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button onClick={() => openEditPage(item)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100">Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-100">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredQuestions.length === 0 && <div className="rounded-2xl bg-slate-50 p-8 text-center text-sm font-bold text-slate-500">No questions found.</div>}
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <Card title="How questions reach student">
            <div className="space-y-3 text-sm font-bold leading-6 text-slate-600">
              <p>1. Question Bank me Published question add karo.</p>
              <p>2. Test Series me question assign karo.</p>
              <p>3. Student Start Test karega, Quiz API se wahi questions aayenge.</p>
            </div>
          </Card>
          <Card title="Admin Tools">
            <div className="grid gap-3">
              {["Bulk Upload Questions", "Import from Excel", "Duplicate Check"].map((item) => <button key={item} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 hover:bg-blue-50">{item} →</button>)}
            </div>
          </Card>
        </aside>
      </section>
    </div>
  );
};

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    <h2 className="mb-5 text-xl font-black text-slate-950">{title}</h2>
    {children}
  </div>
);

const StatCard = ({ title, value, note }: { title: string; value: string; note: string }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
    <p className="text-sm font-bold text-slate-500">{title}</p>
    <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
    <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
  </div>
);

const FilterButtons = ({ title, options, active, onChange }: { title: string; options: string[]; active: string; onChange: (value: string) => void }) => (
  <div>
    <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">{title}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((item) => <button key={item} onClick={() => onChange(item)} className={`rounded-2xl px-3 py-2 text-xs font-black transition ${active === item ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{item}</button>)}
    </div>
  </div>
);

const TypeBadge = ({ type }: { type: QuestionType }) => <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{type}</span>;
const DifficultyBadge = ({ difficulty }: { difficulty: Difficulty }) => <span className={`rounded-full px-3 py-1 text-xs font-black ${difficulty === "Easy" ? "bg-emerald-50 text-emerald-700" : difficulty === "Medium" ? "bg-orange-50 text-orange-700" : "bg-red-50 text-red-700"}`}>{difficulty}</span>;
const StatusBadge = ({ status }: { status: QuestionStatus }) => <span className={`rounded-full px-3 py-1 text-xs font-black ${status === "Published" ? "bg-emerald-50 text-emerald-700" : status === "Review" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}>{status}</span>;

const FormInput = ({ label, value, onChange, placeholder, type = "text" }: { label: string; value: string; placeholder: string; type?: string; onChange: (value: string) => void }) => (
  <div>
    <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">{label}</label>
    <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white" />
  </div>
);

const SelectInput = ({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) => (
  <div>
    <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white">
      {options.map((item) => <option key={item}>{item}</option>)}
    </select>
  </div>
);

export default AdminQuestionBank;
